/**
 * Import-failed panel — rendered when `rescueImportPetsWithChips`
 * returns `validationFailed: true`. The import is all-or-nothing: any
 * row failure rolls back the entire transaction, so `rows` contains
 * the full set of failing rows and `errors[]` per row lists every
 * independent problem detected. The rescue can fix the whole row in
 * one pass and re-upload.
 *
 * The status column reuses the same tooltip pill as the upload-review
 * table — single-error rows surface the message inline; multi-error
 * rows show "N errors" with a hover tooltip listing every issue.
 */

import React, { useMemo } from 'react';
import Button from '../../../components/Button/Button';
import { ModalActions } from '../../../components/Modal/Modal';
import type { RescueImportPetsWithChipsRowResult } from '../../../lib/integrationQueries';
import { StatusPill, type TooltipError } from './ErrorTooltip';
import { ErrorCircleIcon } from './icons';
import { displayRowNumber, type ParsedRow } from './validateRow';
import { truncate, valueOrDash } from './uiHelpers';
import styles from '../Integrations.module.scss';

interface ImportFailedProps {
  /** Per-row backend results — all failures under all-or-nothing. */
  resultRows: RescueImportPetsWithChipsRowResult[];
  /** Parsed CSV rows from the just-submitted batch. Used to enrich the
   *  failure table with pet + owner identity that the backend response
   *  doesn't echo back. */
  parsedRows: ParsedRow[];
  fileName: string | null;
  onClose: () => void;
  onUploadAnother: () => void;
}

interface FailedRowDisplay {
  rowIndex: number;
  chipSerialNumber: string;
  petLabel: string;
  ownerName: string;
  errors: string[];
}

// Truncation cap for the inline single-error label. Keeps the sticky
// Status column from blowing out wide rows when one chip rejection
// message gets verbose ("microchip 981… is registered to ABC Rescue").
const INLINE_LABEL_MAX = 32;

function statusLabelFor(errors: readonly string[]): string {
  if (errors.length === 0) {
    return 'Issue';
  }
  if (errors.length === 1) {
    return truncate(errors[0], INLINE_LABEL_MAX);
  }
  return `${errors.length} errors`;
}

export default function ImportFailed({
  resultRows,
  parsedRows,
  fileName,
  onClose,
  onUploadAnother,
}: ImportFailedProps) {
  // Join backend results to the parsed input by rowIndex so the failure
  // table can show pet + owner names alongside the chip serial — the
  // backend response only carries rowIndex + chipSerial + errors.
  //
  // We intentionally ignore any `petId` echoed back on a failed row:
  // all-or-nothing means nothing was written.
  const failed: FailedRowDisplay[] = useMemo(() => {
    const byIndex = new Map(parsedRows.map((r) => [r.rowIndex, r]));
    return resultRows.map((r) => {
      const parsed = byIndex.get(r.rowIndex);
      return {
        rowIndex: r.rowIndex,
        chipSerialNumber: r.chipSerialNumber ?? parsed?.chipSerialNumber ?? '',
        petLabel: parsed ? [parsed.name, parsed.breed].filter(Boolean).join(' · ') : '',
        ownerName: parsed ? [parsed.ownerFirstName, parsed.ownerLastName].filter(Boolean).join(' ') : '',
        errors: r.errors,
      };
    });
  }, [resultRows, parsedRows]);

  const failureCount = failed.length;

  return (
    <div className={`${styles.wizardStep} ${styles.uploadStep}`}>
      <p className={styles.wizardText}>
        <strong>
          {failureCount} row{failureCount === 1 ? '' : 's'}
        </strong>{' '}
        in <strong>{fileName ?? 'your upload'}</strong> couldn&rsquo;t be published. No chips were registered and no
        emails were sent.
      </p>

      <div className={styles.validationSummary}>
        <div className={`${styles.summaryPill} ${styles.summaryPillError}`}>
          <ErrorCircleIcon />
          <span className={styles.summaryPillCount}>{failureCount}</span>
          <span>failed</span>
        </div>
      </div>

      <div className={styles.reviewCard}>
        <div className={styles.reviewScroll}>
          <table className={styles.reviewTable}>
            <thead>
              <tr>
                <th className={styles.reviewColRow}>Row</th>
                <th>Microchip number</th>
                <th>Pet</th>
                <th>Owner</th>
                <th className={styles.reviewColStatus}>Error</th>
              </tr>
            </thead>
            <tbody>
              {failed.map((r) => {
                // Wrap backend strings as the loose `TooltipError` shape so
                // the StatusPill tooltip can render them alongside the
                // validation-side errors (same component, same styling).
                const tooltipErrors: TooltipError[] = r.errors.map((message) => ({ message }));
                return (
                  <tr key={r.rowIndex} className={styles.reviewRowError}>
                    <td className={styles.reviewColRow}>{displayRowNumber(r.rowIndex)}</td>
                    <td className={styles.mono}>{valueOrDash(r.chipSerialNumber)}</td>
                    <td>
                      <span className={styles.ellip}>{valueOrDash(r.petLabel)}</span>
                    </td>
                    <td>
                      <span className={styles.ellip}>{valueOrDash(r.ownerName)}</span>
                    </td>
                    <td className={styles.reviewColStatus}>
                      <StatusPill
                        isError
                        label={statusLabelFor(r.errors)}
                        errors={tooltipErrors}
                        rowIndex={r.rowIndex}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className={styles.failureCallout}>
        <div className={styles.failureCalloutIcon}>
          <ErrorCircleIcon />
        </div>
        <div className={styles.failureCalloutText}>
          <strong>Fix and re-upload.</strong> Each row&rsquo;s problems are listed in the Error column; rows with more
          than one issue stack them in the hover tooltip. Edit your CSV to address them, then upload the corrected file.
        </div>
      </div>

      <ModalActions>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        <Button variant="green" onClick={onUploadAnother}>
          Upload another
        </Button>
      </ModalActions>
    </div>
  );
}
